<template>
  <span
    v-if="hasDownloadLogId"
    class="download-password-button"
  >
    <el-button
      type="text"
      :disabled="loading || disabled"
      class="download-password-trigger"
      @click="showPassword"
    >
      <i class="el-icon-view"></i>
      {{ loading ? $t('downloadLogUi.loading') : $t('downloadLogUi.showPassword') }}
    </el-button>

    <vuestic-modal
      ref="passwordModal"
      alignModal="download-password-modal"
      :no-buttons="true"
    >
      <div slot="title">{{ $t('downloadLogUi.zipPassword') }}</div>
      <div class="download-password-content">
        <el-input
          :value="password"
          readonly
        >
          <el-button
            slot="append"
            class="copy-button"
            :class="{ 'is-copied': copied }"
            @click="copyPassword"
          >
            <i :class="copied ? 'el-icon-check' : 'el-icon-document-copy'"></i>
            {{ copied ? $t('downloadLogUi.copied') : $t('downloadLogUi.copy') }}
          </el-button>
        </el-input>
      </div>
    </vuestic-modal>
  </span>
</template>

<script>
import { request } from '@/utils/utils.js'

export default {
  name: 'DownloadPasswordButton',
  props: {
    downloadLogId: {
      type: [String, Number],
      default: '',
    },
    apiUrl: {
      type: String,
      default: 'api/dashboard/download-log/zip-password',
    },
    paramName: {
      type: String,
      default: 'download_log_id',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      loading: false,
      password: '',
      copied: false,
      copyTimer: null,
    }
  },
  beforeDestroy () {
    clearTimeout(this.copyTimer)
  },
  computed: {
    hasDownloadLogId () {
      return this.downloadLogId !== '' && this.downloadLogId !== null && this.downloadLogId !== undefined
    },
  },
  methods: {
    async showPassword () {
      if (this.loading || this.disabled || !this.hasDownloadLogId) return

      this.loading = true
      const token = localStorage.getItem('Authorization')
      try {
        const response = await request({
          method: 'post',
          url: `${this.apiUrl}?access_token=${token}`,
          params: {
            [this.paramName]: this.downloadLogId,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        })
        if (!response) return

        const { code, data, msg } = response.data
        if (String(code) !== '10000' || !data) {
          throw new Error(msg || this.$t('downloadLogUi.passwordUnavailable'))
        }

        this.password = data
        this.$refs.passwordModal.open()
      } catch (error) {
        this.showToast(error.message || this.$t('downloadLogUi.fetchPasswordFailed'), {
          icon: 'fa-exclamation-circle',
          position: 'top-center',
          duration: 3000,
          fullWidth: false,
        })
      } finally {
        this.loading = false
      }
    },
    copyPassword () {
      if (!this.password) return

      const input = document.createElement('input')
      document.body.appendChild(input)
      input.setAttribute('value', this.password)
      input.select()
      try {
        document.execCommand('copy')
        this.copied = true
        clearTimeout(this.copyTimer)
        this.copyTimer = setTimeout(() => {
          this.copied = false
        }, 1500)
      } catch (error) {
        this.showToast(this.$t('downloadLogUi.copyFailed'), {
          icon: 'fa-exclamation-circle',
          position: 'top-center',
          duration: 3000,
          fullWidth: false,
        })
      }
      document.body.removeChild(input)
    },
  },
}
</script>

<style lang="scss" scoped>
.download-password-button {
  display: inline-block;

  .download-password-trigger {
    padding: 0;
    font-size: inherit;
    color: $theme-blue;

    &:hover,
    &:focus {
      color: lighten($theme-blue, 8%);
    }

    &.is-disabled {
      color: $gray;
    }
  }

  .el-icon-view {
    margin-right: 4px;
  }
}

/deep/ .download-password-modal {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  .modal-dialog {
    width: 100%;
    max-width: 620px;
  }
}

.download-password-content {
  padding: 4px 0;

  /deep/ .el-input__inner {
    height: 40px;
    line-height: 40px;
    background-color: $light-gray2;
    border-color: $lighter-gray;
    color: $darkest-gray;
  }

  /deep/ .el-input-group__append {
    padding: 0;
    border: none;
    background-color: transparent;
  }

  .copy-button {
    height: 40px;
    min-width: 118px;
    padding: 0 20px;
    border-radius: 0 4px 4px 0;
    background-color: $vue-green;
    border-color: $vue-green;
    color: $white;
    transition: background-color 0.2s ease, transform 0.1s ease;

    &:hover,
    &:focus {
      background-color: lighten($vue-green, 6%);
      border-color: lighten($vue-green, 6%);
    }

    &:active {
      transform: scale(0.97);
    }

    &.is-copied {
      background-color: darken($vue-green, 8%);
      border-color: darken($vue-green, 8%);
    }

    i {
      margin-right: 4px;
    }
  }
}
</style>
